import React, { memo, useEffect, useRef, useState } from "react";
import "./styles/Queries.css";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { Query } from "../../../../../interfaces/ProductObject";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../dataManagement/uiDb/UiDb";
import {
  postAskQuery,
  postAnswerQuery,
} from "../../../../../services/thunks/ProductDetailsThunks";
import genericUtility from "../../../../../services/utility/GenericUtility";
import { openAuth } from "../../../../../dataManagement/uiDb/GeneralDb";
interface QueriesProps {
  queries: Query[];
  productId?: string;
  handleQueryChange?: (newQuery: Query) => void;
  canAsk?: boolean;
  canAnswer?: boolean;
  /** Section heading; defaults to "Community Q&A". */
  title?: string;
}

const Queries: React.FC<QueriesProps> = ({
  queries,
  productId,
  handleQueryChange,
  canAsk,
  canAnswer,
  title = "Community Q&A",
}) => {
  const dispatch: AppDispatch = useDispatch();

  // Optimistic-only local additions — merged with prop queries for display
  const [localAdditions, setLocalAdditions] = useState<Query[]>([]);

  const [newQuestion, setNewQuestion] = useState("");
  const [answeringIdx, setAnsweringIdx] = useState<number | null>(null);
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const answerInputRef = useRef<HTMLInputElement | null>(null);

  // Sorted view of prop queries (the source of truth from Redux)
  const sortedPropQueries = [...(queries || [])].sort((a: any, b: any) => {
    const aTime = Date.parse(a?.timestamp || a?.createdAt || "") || 0;
    const bTime = Date.parse(b?.timestamp || b?.createdAt || "") || 0;
    return aTime - bTime;
  });

  // Once Redux reflects the new question, drop it from local additions
  useEffect(() => {
    if (localAdditions.length === 0) return;
    setLocalAdditions((prev) =>
      prev.filter(
        (local) =>
          !sortedPropQueries.some(
            (q) => q.question.trim() === local.question.trim()
          )
      )
    );
  }, [queries]);

  // Displayed list = Redux data + any optimistic additions not yet in Redux
  const qas = [...sortedPropQueries, ...localAdditions];

  useEffect(() => {
    if (answeringIdx !== null) answerInputRef.current?.focus();
  }, [answeringIdx]);

  const handleAskQuestion = async () => {
    if (genericUtility.isUserLoggedIn() === false) {
          dispatch(openAuth());
          return;
        }
    const trimmed = newQuestion.trim();
    if (!trimmed) return;
    setLocalAdditions((prev) => [...prev, { question: trimmed }]);
    setNewQuestion("");
    if (productId) {
      const buyerId = genericUtility.getCookie("userId") || genericUserId();
      dispatch(postAskQuery({ productId, buyerId, question: trimmed }));
    } else if (handleQueryChange) {
      handleQueryChange({ question: trimmed });
    }
  };

  const handleAnswer = (idx: number) => {
    if (genericUtility.isUserLoggedIn() === false) {
      dispatch(openAuth());
      return;
    }
    if (!canAnswer) return;
    const trimmed = answerText.trim();
    if (!trimmed) return;
    const target = qas[idx];
    if (handleQueryChange) handleQueryChange({ ...target, answer: trimmed });
    if (productId) {
      const queryId = selectedQueryId || (target as any)?.id || (target as any)?.queryId;
      if (queryId) {
        dispatch(postAnswerQuery({ productId, queryId, answer: trimmed }));
      }
    }
    setAnsweringIdx(null);
    setAnswerText("");
    setSelectedQueryId(null);
  };

  const genericUserId = () =>
    typeof window !== "undefined" ? localStorage.getItem("userId") || "" : "";

  function formatTimeAgo(isoString?: string): string {
    if (!isoString) return "";
    const then = new Date(isoString).getTime();
    const diff = Math.max(0, Date.now() - then);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  return (
    <div className="queries-container-wrapper">
      <div
        className="queries-header collapsible"
        onClick={() => setIsCollapsed((prev) => !prev)}
        style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
      >
        {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
        <h3 style={{ margin: 0 }}>{title}</h3>
      </div>

      {!isCollapsed && (
        <>
          <div className="qa-conversation">
            {qas.map((qa, idx) => (
              <div className="qa-block" key={idx}>
                <div className="msg-row left">
                  <div className="msg-content">
                    <span className="msg-badge q" aria-hidden title="Question">Q</span>
                    <div
                      className={`msg-bubble q accent${canAnswer && !qa.answer ? " clickable" : ""}`}
                      onClick={() => {
                        if (canAnswer && !qa.answer) {
                          setAnsweringIdx(idx);
                          setAnswerText("");
                          setSelectedQueryId((qa as any)?.id || (qa as any)?.queryId || null);
                        }
                      }}
                    >
                      <span className="msg-label">Question</span>
                      <span className="msg-text">{qa.question}</span>
                      <span className="msg-footer">
                        {qa.timestamp && (
                          <span className="msg-meta">{formatTimeAgo(qa.timestamp)}</span>
                        )}
                        <span
                          className={`msg-ticks${qa.answer ? " msg-ticks--read" : ""}`}
                          aria-label={qa.answer ? "Answered" : "Awaiting answer"}
                        >
                          <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                            <path d="M1 5l3 3L10 1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 5l3 3 6-7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {qa.answer ? (
                  <div className="msg-row right">
                    <div className="msg-content reverse">
                      <div className="msg-bubble a accent">
                        <span className="msg-label">Answer</span>
                        <span className="msg-text">{qa.answer}</span>
                        {qa.timestamp && (
                          <span className="msg-meta">{formatTimeAgo(qa.timestamp)}</span>
                        )}
                      </div>
                      <span className="msg-chip a" aria-hidden title="Answer">A</span>
                    </div>
                  </div>
                ) : (
                  canAnswer && (
                    <div className="msg-row left">
                      <button
                        className="qa-reply-prompt"
                        onClick={() => {
                          setAnsweringIdx(idx);
                          setAnswerText("");
                          setSelectedQueryId((qa as any)?.id || (qa as any)?.queryId || null);
                        }}
                      >
                        <span className="qa-reply-prompt__icon">💬</span>
                        <span>Tap to reply</span>
                      </button>
                    </div>
                  )
                )}

                {qa.answer && <div className="qa-connector" aria-hidden />}
              </div>
            ))}
          </div>

          {canAnswer && qas.length === 0 && (
            <p className="qa-empty-state">No questions yet — check back later.</p>
          )}

          {((canAnswer && answeringIdx !== null) || (canAsk && !canAnswer)) && (
            <div
              className="single-composer"
              role="form"
              aria-label={canAnswer ? "Answer a question" : "Ask a question"}
            >
              {canAnswer && answeringIdx !== null && (
                <div className="answer-context">
                  <span>Replying to:</span>
                  <span className="q-snippet">{qas[answeringIdx]?.question ?? ""}</span>
                  <button
                    className="composer-cancel"
                    onClick={() => { setAnsweringIdx(null); setAnswerText(""); setSelectedQueryId(null); }}
                    aria-label="Cancel answering"
                  >×</button>
                </div>
              )}
              <form
                className="composer-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (canAnswer && answeringIdx !== null) handleAnswer(answeringIdx);
                  else if (canAsk && !canAnswer) handleAskQuestion();
                }}
              >
                <input
                  className="composer-input"
                  type="text"
                  placeholder={canAnswer ? "Type your answer and press Enter..." : "Type your question and press Enter..."}
                  value={canAnswer ? answerText : newQuestion}
                  onChange={(e) => canAnswer ? setAnswerText(e.target.value) : setNewQuestion(e.target.value)}
                  ref={canAnswer ? answerInputRef : undefined}
                />
                <button type="submit" className="composer-send" aria-label={canAnswer ? "Send answer" : "Send question"}>
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default memo(Queries);
